// Libraries
import React from 'react';
import {css} from 'aphrodite/no-important';
// Core
import {randomElementFromArray, randomElementsFromArray} from '../core/Utilities';
import CoreMetrics, {FormFactorChangeListener, sharedFormFactorChangeListener, createExpandableMediaQueryStylesheet} from '../core/Metrics';
import ContentData from '../core/ContentData';
import CoreStyles from '../core/Styles';
import {HeadingMediumText, BodyText, LabelMediumText, LabelSmallText} from '../core/Type';
import {FixedSpace, FlexibleSpace} from '../core/Spacing';
// Icons
import ContentIcon from '../icons/ContentIcon';
// Control
import Button from '../controls/Button';
// Layouts
import {Grid, GridRow, GridColumn, DockedTOCandContentLayout} from '../layouts/Grid';
import {CssGrid, CssGridRow, CssGridColumn} from '../layouts/CssGrid';
import MainAndSidebarLayout from '../layouts/MainAndSidebarLayout';
import TwoColumnLayout from '../layouts/TwoColumnLayout';
import OneContentColumnLayout from '../layouts/OneContentColumnLayout';
// Modules
import TopicHeader from '../modules/TopicHeader';
import SiteBar from '../modules/SiteBar';
import SiteFooter from '../modules/SiteFooter';

export default class UnitPage extends React.Component {
    render() {
        const styles = createExpandableMediaQueryStylesheet({
            page: {
                display: "flex",
                flexDirection: "column-reverse",
            },
            contentColumn: {
                display: "flex",
                flexDirection: "column",
            },
            phoneLayout: {
                display: "none",

                [CoreMetrics.MediaQuery().Phone]: {
                    display: "block",
                },
            },
            tabletLayout: {
                display: "none",

                [CoreMetrics.MediaQuery().Tablet]: {
                    display: "block",
                },
            },
            desktopLayout: {
                display: "none",

                [CoreMetrics.MediaQuery().Desktop]: {
                    display: "block",
                },
            },
            withOuterMargin: {
                paddingLeft: CoreMetrics.Grid.HorizontalMargin,
                paddingRight: CoreMetrics.Grid.HorizontalMargin,
            },
        });

        let page = null;
        const sidebarBehavior = sharedFormFactorChangeListener
            .getSidebarBehavior();
        if (sidebarBehavior ===
            FormFactorChangeListener.SidebarBehavior.Docked
        ) {
            page = (
                <div className={css(styles.page)}>
                    <DockedTOCandContentLayout toc={(
                        <UnitTOC variant={UnitTOC.Variant.Docked} />
                    )} content={(
                        <div className={css(styles.contentColumn)}>
                            <Grid>
                                <GridRow>
                                    <GridColumn>
                                        <WelcomeModule />
                                        <Lesson />
                                        <Lesson />
                                        <Lesson />
                                    </GridColumn>
                                </GridRow>
                            </Grid>
                            <SiteFooter />
                        </div>
                    )} />
                    <TopicHeader variant={TopicHeader.Variant.Stretched} />
                    <SiteBar variant={SiteBar.Variant.Stretched} />
                </div>
            );
        } else {
            const mainContent = <div>
                <WelcomeModule />
                <Lesson />
                <Lesson />
                <Lesson />
            </div>;

            const phoneView = <div
                className={css(styles.phoneLayout, styles.withOuterMargin)}
            >
                <WelcomeModule />
                <UnitTOC />
                <Lesson />
                <Lesson />
                <Lesson />
            </div>;
            const noSidebarTabletView = <div
                className={css(styles.tabletLayout, styles.withOuterMargin)}
            >
                <OneContentColumnLayout
                    column1={<div>
                        <WelcomeModule />
                        <UnitTOC />
                        <Lesson />
                        <Lesson />
                        <Lesson />
                    </div>}
                />
            </div>;
            const desktopView = <div className={css(styles.desktopLayout)}>
                <MainAndSidebarLayout
                    sidebarContent={<UnitTOC/>}
                    mainContent={mainContent}
                />
            </div>;

            page = (
                <div className={css(styles.page)}>
                    <SiteFooter />

                    {phoneView}
                    {noSidebarTabletView}
                    {desktopView}

                    <TopicHeader />
                    <SiteBar />
                </div>
            );
        }
        return page;
    }
}

class UnitTOC extends React.Component {
    static Variant = {
        InGrid: "inGrid", // Default variant
        Docked: "docked",
    };
    render() {
        const styles = createExpandableMediaQueryStylesheet({
            dockedContainer: {
                backgroundColor: CoreMetrics.Color.Neutral.OffWhite,
                height: "100%",
            },
            header: {
                paddingTop: CoreMetrics.ElementPadding.XLarge(),
                paddingBottom: CoreMetrics.ElementPadding.Medium(),
            },
            dockedHeader: {
                paddingLeft: CoreMetrics.Grid.HorizontalMargin,
                paddingRight: CoreMetrics.Grid.HorizontalMargin,
            },
            dockedRow: {
                paddingLeft: CoreMetrics.Grid.HorizontalMargin,
                paddingRight: CoreMetrics.Grid.HorizontalMargin,
            },
        });

        const {variant} = this.props;
        const containerStyles = [
            variant === UnitTOC.Variant.Docked
                && styles.dockedContainer,
            variant === UnitTOC.Variant.Docked
                && CoreStyles.hairlineOuterStroke,
        ];
        const headerStyles = [
            styles.header,
            CoreStyles.hairlineStrokeBottom,
            variant === UnitTOC.Variant.Docked && styles.dockedHeader,
        ];
        const rowStyles = [
            variant === UnitTOC.Variant.Docked && styles.dockedRow,
        ];

        const exerciseTitles = randomElementsFromArray(ContentData.Lessons, 12);

        return (
            <div className={css(containerStyles)}>
                <div className={css(headerStyles)}>
                    <HeadingMediumText>Progress</HeadingMediumText>
                </div>
                <div>
                    {exerciseTitles.map((title, index) => <LessonSummaryRow
                        key={`exercise${index}`}
                        styles={rowStyles}
                        title={title}
                        selected={index === 1 && true}
                    />)}
                </div>
            </div>
        );
    }
}
UnitTOC.defaultProps = {
    variant: UnitTOC.Variant.InGrid,
};

class LessonSummaryRow extends React.Component {
    render() {
        const title = this.props.title;
        const styles = createExpandableMediaQueryStylesheet({
            container: {
                paddingTop: CoreMetrics.ElementPadding.Medium(),
                paddingBottom: CoreMetrics.ElementPadding.Medium(),
            },
            selectedTitle: {
                color: CoreMetrics.Color.Control.Default,
            },
        });

        const containerStyles = [
            styles.container,
            CoreStyles.hairlineStrokeBottom,
            ...this.props.styles,
        ];
        const labelStyles = [];
        if (this.props.selected) {
            labelStyles.push(styles.selectedTitle);
        }

        return (
            <div className={css(containerStyles)}>
                <BodyText styles={labelStyles}>{title}</BodyText>
            </div>
        );
    }
}
LessonSummaryRow.defaultProps = {
    styles: [],
};

class WelcomeModule extends React.Component {
    render() {
        const styles = createExpandableMediaQueryStylesheet({
            container: {
                paddingTop: CoreMetrics.ElementPadding.XLarge(),
                paddingBottom: CoreMetrics.ElementPadding.XLarge(),

                [CoreMetrics.MediaQuery().Phone]: {
                    textAlign: "center",
                },
            },
            centeredOnTablet: {
                [CoreMetrics.MediaQuery().Tablet]: {
                    textAlign: "center",
                },
            },
            wideOnPhone: {
                [CoreMetrics.MediaQuery().Phone]: {
                    width: "100%",
                },
            },
            wideOnTablet: {
                [CoreMetrics.MediaQuery().Tablet]: {
                    width: "100%",
                },
            },
        });

        return (
            <div
                className={css(
                    styles.container,
                    CoreStyles.hairlineStrokeBottom,
                    styles.centeredOnTablet,
                )}
            >
                <div>
                    <LabelSmallText>Start your learning journey</LabelSmallText>
                    <HeadingMediumText>
                        Unlock your personalized study plan
                    </HeadingMediumText>

                    <FixedSpace variant={FixedSpace.Variant.Vertical} />

                    <BodyText
                        styles={[
                            CoreStyles.gridWidthOneHalf,
                            styles.wideOnPhone,
                            styles.wideOnTablet,
                        ]}
                    >
                        Take a test to identify your areas for growth. We will
                        recommend lessons for exactly what you need to learn.
                    </BodyText>

                    <FixedSpace variant={FixedSpace.Variant.Vertical} />

                    <Button
                        title="Take diagnostic test"
                        styles={[styles.wideOnPhone, styles.wideOnTablet,]}
                    />
                </div>
            </div>
        );
    }
}

class Lesson extends React.Component {
    render() {
        const styles = createExpandableMediaQueryStylesheet({
            container: {
                paddingTop: CoreMetrics.ElementPadding.XLarge(),
                paddingBottom: CoreMetrics.ElementPadding.XLarge(),
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
            },
            content: {
                display: "flex",
                flexDirection: "row",
                alignItems: "flex-start",
            },
        });

        return <div
            className={css(
                styles.container,
                CoreStyles.hairlineStrokeBottom,
            )}
        >
            <HeadingMediumText>
                {randomElementFromArray(ContentData.Lessons)}
            </HeadingMediumText>
            <FixedSpace variant={FixedSpace.Variant.Vertical} />

            <CssGrid sizePhone={4} sizeTablet={8} size={9} >
                <CssGridRow>
                    <CssGridColumn sizePhone={4} sizeTablet={8} size={4}>
                        <Learn />
                    </CssGridColumn>
                    <CssGridColumn sizePhone={0} sizeTablet={0} size={5}>
                        <Practice />
                        <Practice />
                    </CssGridColumn>
                </CssGridRow>

                <CssGridRow>
                    <CssGridColumn sizePhone={4} sizeTablet={8} size={0}>
                        <Practice />
                        <Practice />
                    </CssGridColumn>
                </CssGridRow>
            </CssGrid>
        </div>;
    }
}

class Learn extends React.Component {
    render() {
        const styles = createExpandableMediaQueryStylesheet({
            container: {
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
            },
            item: {
                paddingTop: CoreMetrics.ElementPadding.XSmall(),
                paddingBottom: CoreMetrics.ElementPadding.XSmall(),
            },
        });
        let combinedStyles = [styles.container];
        if (this.props.styles != null) {
            combinedStyles = combinedStyles.concat(this.props.styles);
        }

        const contentTitles = randomElementsFromArray(ContentData.Content, 8);
        return (
            <div className={css(combinedStyles)}>
                <LabelSmallText>Learn</LabelSmallText>
                <FixedSpace
                    size={FixedSpace.Size.Small}
                    variant={FixedSpace.Variant.Vertical}
                />
                {contentTitles.map((title, index) => <ContentItem
                    key={`contentItem${index}`}
                    styles={[styles.item]}
                    title={title}
                />)}
            </div>
        );
    }
}

class ContentItem extends React.Component {
    render() {
        const title = this.props.title;
        const styles = createExpandableMediaQueryStylesheet({
            container: {
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
            },
        });
        let combinedStyles = [styles.container];
        if (this.props.styles != null) {
            combinedStyles = combinedStyles.concat(this.props.styles);
        }
        return (
            <div className={css(combinedStyles)}>
                <ContentIcon />
                <FixedSpace />
                <BodyText>{title}</BodyText>
            </div>
        );
    }
}

class Practice extends React.Component {
    render() {
        const styles = createExpandableMediaQueryStylesheet({
            container: {
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
            },
        });
        let combinedStyles = [styles.container];

        if (this.props.styles != null) {
            combinedStyles = combinedStyles.concat(this.props.styles);
        }
        return (
            <div className={css(combinedStyles)}>
                <LabelSmallText>Practice</LabelSmallText>
                <FixedSpace variant={FixedSpace.Variant.Vertical} />
                <PracticeItem
                    title={randomElementFromArray(ContentData.Content)}
                />
                <FixedSpace variant={FixedSpace.Variant.Vertical} />
                <PracticeItem
                    title="Instantaneous velocity and instantaneous speed"
                />
            </div>
        );
    }
}

class PracticeItem extends React.Component {
    render() {
        const styles = createExpandableMediaQueryStylesheet({
            container: {
                padding: CoreMetrics.ElementPadding.Medium(),
                backgroundColor: CoreMetrics.Color.LightUI.SecondaryBG,
                borderRadius: CoreMetrics.CornerRadius,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
            },
            titleContainer: {
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
            },
            secondRow: {
                alignSelf: "stretch",
                paddingLeft: 24,
                display: "flex",
                flexDirection: "row",
            },
            practiceButton: {
                color: CoreMetrics.Color.Control.Default,
            },
        });
        let combinedStyles = [styles.container];

        if (this.props.styles != null) {
            combinedStyles = combinedStyles.concat(this.props.styles);
        }

        const title = this.props.title;
        return (
            <div className={css(combinedStyles)}>
                <div className={css(styles.titleContainer)}>
                    <ContentIcon />
                    <FixedSpace />
                    <LabelMediumText>{title}</LabelMediumText>
                </div>
                <FixedSpace
                    size={FixedSpace.Size.XXSmall}
                    variant={FixedSpace.Variant.Vertical}
                />
                <div className={css(styles.secondRow)}>
                    <FixedSpace />
                    <LabelSmallText>7 questions</LabelSmallText>
                    <FlexibleSpace />
                    <LabelSmallText styles={[styles.practiceButton]}>
                        Practice
                    </LabelSmallText>
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/demo/UnitPage.jsx